import {
    Box,
    Button, FormControl, FormLabel, InputLabel,
    List,
    ListItem, ListItemButton,
    ListItemText,
    MenuItem,
    Select, SelectChangeEvent,
    TextField,
    Typography
} from "@mui/material";
import type {KeyboardEvent} from "react";
import {createRef, ChangeEvent, RefObject, useCallback, use, useEffect, useState} from "react";
import {fetchAutosuggestions} from "services/khwApi";
import {translateKey} from "utils/fieldsFormatters";
import {LanguageContext} from "context/LanguageContext";
import {DESCRIPTOR_ICONS_BY_TYPE, DescriptorType, SuggestionObject} from "consts/khw-consts";
import {useNavigate} from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import TopicIcon from "@mui/icons-material/Topic";
import {useDetectClickOutside} from "react-detect-click-outside";
import InfoTooltip from "./InfoTooltip";
import {useCookies} from 'react-cookie';
import ReactGA from "react-ga4";


interface AutocompleteSearchProps {
    size?: 'large' | 'medium'
    showSearchBtn?: boolean
    showPlaceholder?: boolean
    extraAction?: () => void
    showCTAButtons?: boolean
    showInListCTAButtons?: boolean
    viewMode: 'desktop_homepage' | 'mobile_homepage' | 'desktop_nav' | 'mobile_nav'
}


const AutocompleteSearch = (props: AutocompleteSearchProps) => {
    const {language} = use(LanguageContext);
    const {size, showSearchBtn, showPlaceholder, extraAction, showCTAButtons, showInListCTAButtons, viewMode} = props
    const navigate = useNavigate()
    const placeholder = showPlaceholder ? translateKey('searchPlaceholderText', language) : `${translateKey('searchPlaceholderTextShort', language)}...`
    const inputSize = size === 'large' ? 'medium' : 'small'
    const [suggestions, setSuggestions] = useState<SuggestionObject[]>([])
    const [value, setValue] = useState('')
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [cookies, setCookie] = useCookies(['source']);
    const [selectedSource, setSelectedSource] = useState(cookies.source ?? 'DBN');
    const closeDropdown = () => {
        setSuggestions([])
        setValue('')
    }

    const [timeoutId, setTimeoutId] = useState<any | null>(null)

    const ref = useDetectClickOutside({onTriggered: closeDropdown});

    const listItemsRefs = suggestions.reduce((acc: RefObject<SuggestionObject | null>[], value, id) => {
        acc[id] = createRef();
        return acc;
    }, []);


    const refetchSuggestions = useCallback((newValue : string) => {

            if (newValue && newValue.length >= 3) {
                fetchAutosuggestions(newValue, selectedSource)
                    .then((response) => {
                        const {suggests} = response.data
                        const suggestions = Object.entries(suggests).map(([key, newValue]) => newValue as SuggestionObject).flat()
                        setSuggestions(suggestions)
                    })
            }
        }
        , [selectedSource])


    useEffect(() => {
        // @ts-ignore
        if (listItemsRefs[selectedIndex] !== undefined) {
            // @ts-ignore
            listItemsRefs[selectedIndex].current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            })
        }
    }, [selectedIndex, listItemsRefs])

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const {value} = event.target
        setValue(value)
        clearTimeout(timeoutId)
        setTimeoutId(setTimeout(()=> refetchSuggestions(value), process.env.REACT_APP_DEBOUNCE_TIME_IN_MS))
    }

    const clearData = () => {
        clearTimeout(timeoutId)
        setSuggestions([])
        setSelectedIndex(-1)
        setValue('')
    }

    const navigateToDetails = (option: SuggestionObject) => {
        clearData()
        navigate(`/descriptor-details/${option.nlpId}`)

        ReactGA.event({
            category: 'SearchWithSuggestions',
            action: 'Chosen suggested descriptor',
            label: option.preferredName
        });

        if (extraAction) {
            extraAction()
        }
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            if (selectedIndex > -1) {
                navigateToDetails(suggestions[selectedIndex])
            } else {
                onSearchBtnClick()
            }
        } else if (event.key === 'ArrowDown') {
            setSelectedIndex(selectedIndex + 1)
        } else if (event.key === 'ArrowUp') {
            setSelectedIndex(selectedIndex - 1)
        }
    }

    const onSearchBtnClick = () => {
        clearData()
        if (extraAction) {
            extraAction()
        }
        ReactGA.event({
            category: 'FullSearch',
            action: 'Full search action executed',
            label: value
        });

        if (selectedSource === 'all') {
            if (value.length > 0){
                navigate(`/descriptors?searchedText=${value}`)}
            else{
                navigate(`/descriptors`)
            }
        } else {
            if (value.length > 0) {
                navigate(`/descriptors?searchedText=${value}&sourceName=${selectedSource}`)
            }else {
                navigate(`/descriptors?sourceName=${selectedSource}`)
            }

        }
    }


    const selectSourceName = (event: SelectChangeEvent) => {
        setSelectedSource(event.target.value)
        setCookie('source', event.target.value)
    }

    const renderCTAButtons = (inList?: boolean) => <Box className={inList ? 'inlist-buttons' : 'fixed-buttons'}
                                                        key={'-1'}>

        <Button variant={'contained'} className={'homepage-cta-button'} onClick={onSearchBtnClick}
                startIcon={<SearchIcon/>}>{translateKey('search', language)}</Button>
        {!inList &&
            <Button variant={'outlined'} className={'homepage-cta-button'} onClick={() => navigate('/descriptors')}
                    startIcon={<TopicIcon/>}>{translateKey('browse', language)}</Button>}

    </Box>

    const renderListSubheader = (type: DescriptorType) => <ListItem key={type}>
        <img src={DESCRIPTOR_ICONS_BY_TYPE[type]} className={'icon-in-chip-search'}
             alt={'ogólna grafika deskryptora'}/>
        <ListItemText
            className={'list-subheader'}>{translateKey(type, language)}</ListItemText>
    </ListItem>

    const renderRow = (suggestion: SuggestionObject, index: number) => {
        // @ts-ignore
        return <ListItem className={'suggestion-row'} key={suggestion.nlpId} ref={listItemsRefs[index]}>
            <ListItemButton onClick={() => navigateToDetails(suggestion)} autoFocus={false}
                            selected={selectedIndex === index}
            >
                <Typography
                    dangerouslySetInnerHTML={{__html: suggestion.secondaryName ? `${suggestion.preferredName} ⇐ ${translateKey('SEARCH_NAME_SECONDARY_REDIRECT', language)}: ${suggestion.secondaryName}` : suggestion.preferredName}}/>
            </ListItemButton>
        </ListItem>
    }

    const renderListItems = () => {
        let tmpType = ''
        return suggestions.map((suggestion, index) => {
            if (suggestion.type !== tmpType) {
                tmpType = suggestion.type
                return <Box
                    key={`row-${index}`}>{renderListSubheader(tmpType as DescriptorType)}{renderRow(suggestion, index)}</Box>

            } else {
                return renderRow(suggestion, index)

            }

        })
    }
    const renderExtraInfo = () => <Typography
        className={'search-autosuggestions'}>{translateKey('search.searchAutosuggestions', language)}<InfoTooltip
        text={translateKey('search.autosuggestionsTooltip', language)}/></Typography>

    return <Box className={'column'}>

        <Box className={`search-box`}>
            <Box>
                <FormControl>
                    <FormLabel aria-label={placeholder}>
                        <TextField size={inputSize}
                                   className={'search-input-form'}
                                   placeholder={placeholder}
                                   onChange={handleChange}
                                   onKeyDown={onKeyDown}
                                   value={value}
                                   autoComplete={'off'}
                                   autoFocus
                        /></FormLabel>

                </FormControl>
                {suggestions.length > 0 && <List className={`search-list ${viewMode}-list`} ref={ref}>
                    {renderExtraInfo()}

                    {renderListItems()}
                    {showInListCTAButtons && renderCTAButtons(true)}

                </List>}
            </Box>
            <FormControl>
                <InputLabel id={'source-label'}
                            aria-label={'select-source'}></InputLabel>
                    <Select value={selectedSource} className={'select source-select'} variant={'outlined'}
                            labelId="source-label"
                            size={inputSize}
                            onChange={selectSourceName}>
                        <MenuItem value={'DBN'}>{translateKey('onlyDBN', language)}</MenuItem>
                        <MenuItem value={'MESH'}>{translateKey('onlyMeSH', language)}</MenuItem>
                        <MenuItem value={'all'}>{translateKey('DBN&MeSH', language)}</MenuItem>
                    </Select>
            </FormControl>

            {
                showSearchBtn &&
                <Button variant={'contained'} className={'small-search-btn'} onClick={onSearchBtnClick}
                        startIcon={<SearchIcon/>}>{translateKey('search', language)}</Button>
            }
        </Box>
        {
            showCTAButtons && renderCTAButtons()
        }
    </Box>

}

export default AutocompleteSearch